import type { AnyFieldApi } from "@tanstack/react-form";

import { firstFieldError } from "@/components/form/validators";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils/cn";

interface TextAreaFieldProps {
  field: AnyFieldApi;
  label: string;
  placeholder?: string;
  description?: string;
  rows?: number;
  maxLength?: number;
  className?: string;
}

/**
 * Multi-line counterpart to `@/components/form/TextField`, bound to a TanStack
 * Form field. Used for the profile narrative fields and action comments. Wires
 * accessible label/error association and aria-invalid automatically.
 */
export function TextAreaField({
  field,
  label,
  placeholder,
  description,
  rows = 3,
  maxLength,
  className,
}: TextAreaFieldProps) {
  const error = firstFieldError(field);
  const errorId = `${field.name}-error`;
  const descId = `${field.name}-desc`;
  const invalid = error !== undefined;

  return (
    <div className={cn("space-y-1.5", className)}>
      <Label htmlFor={field.name}>{label}</Label>
      <textarea
        id={field.name}
        name={field.name}
        rows={rows}
        placeholder={placeholder}
        maxLength={maxLength}
        value={(field.state.value as string | undefined) ?? ""}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        aria-invalid={invalid}
        aria-describedby={cn(description && descId, invalid && errorId) || undefined}
        className={cn(
          "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          "aria-[invalid=true]:border-destructive",
        )}
      />
      {description && (
        <p id={descId} className="text-sm text-muted-foreground">
          {description}
        </p>
      )}
      {invalid && (
        <p id={errorId} className="text-sm font-medium text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
